import React, { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, CreditCard, ArrowLeft, Undo2 } from 'lucide-react'
import { toast } from 'sonner'

// UI Components
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge, BadgeProps } from '@/components/ui/badge'

// Services
import {
  listVoucherTypes,
  getVoucher,
  createVoucher,
  updateVoucher
} from '@/app/services/voucherService'
import { listParties } from '@/app/services/partyService'
import { listSites } from '@/app/services/siteService'
import { listItems } from '@/app/services/itemService'
import { listGodowns } from '@/app/services/inventoryService'

// Types
import { VoucherCreate, VoucherUpdate, VoucherResponse } from '@/app/types/voucher'
import { Item } from '@/app/types/item'

// Components
import { PurchaseInvoiceForm } from './PurchaseInvoiceForm'
import { PurchaseInvoiceFormValues } from './schema'
import { QuickPaymentOutModal } from '../components/QuickPaymentOutModal'
import { getSimplifiedSalesInvoiceStatus } from '@/app/lib/statusUtils'

// Extended VoucherResponse interface with optional status fields
interface ExtendedVoucherResponse extends VoucherResponse {
  status?: string
  payment_status?: string
}

const PurchaseInvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditMode = !!id

  // State for quick payment modal
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)

  // Fetch Voucher Type ID for "PINV"
  const { data: voucherTypesData, isLoading: isLoadingVoucherTypes } = useQuery({
    queryKey: ['voucher-types', 'PINV'],
    queryFn: async () => {
      const response = await listVoucherTypes({
        filters: JSON.stringify([{ field: 'code', op: 'eq', value: 'PINV' }]),
        limit: 1
      })
      if (response.items.length > 0) {
        return response.items[0]
      }
      throw new Error('Purchase Invoice voucher type (PINV) not found.')
    }
  })
  const purchaseInvoiceVoucherTypeId = voucherTypesData?.id

  // Fetch Purchase Invoice data if in edit mode
  const {
    data: purchaseInvoice,
    isLoading: isLoadingPurchaseInvoice,
    isError,
    error
  } = useQuery<ExtendedVoucherResponse | null>({
    queryKey: ['purchase-invoice', id],
    queryFn: async (): Promise<ExtendedVoucherResponse | null> => {
      if (!id) return null
      const result = (await getVoucher(Number(id))) as ExtendedVoucherResponse
      return result
    },
    enabled: isEditMode && !!purchaseInvoiceVoucherTypeId
  })

  // Fetch Parties, Sites, Godowns
  const { data: partiesData = { items: [] }, isLoading: isLoadingParties } = useQuery({
    queryKey: ['parties', 'vendors'],
    queryFn: () =>
      listParties({
        limit: 500,
        // Assuming there's a way to filter by party type
        filters: JSON.stringify([{ field: 'is_vendor', op: 'eq', value: true }])
      })
  })

  const { data: sitesData = { items: [] }, isLoading: isLoadingSites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 500 })
  })

  const { data: godownsData = { items: [] }, isLoading: isLoadingGodowns } = useQuery({
    queryKey: ['godowns'],
    queryFn: () => listGodowns({ limit: 500 })
  })

  // Mutations for create/update
  const createMutation = useMutation({
    mutationFn: (data: VoucherCreate) => createVoucher(data),
    onSuccess: () => {
      toast.success('Purchase Invoice created successfully')
      queryClient.invalidateQueries({ queryKey: ['purchase-invoices'] })
      navigate('/purchase-invoices/list')
    },
    onError: (error: Error) => {
      toast.error(`Error creating Purchase Invoice: ${error.message}`)
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: VoucherUpdate }) => updateVoucher(id, data),
    onSuccess: () => {
      toast.success('Purchase Invoice updated successfully')
      queryClient.invalidateQueries({ queryKey: ['purchase-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['purchase-invoice', id] })
      navigate('/purchase-invoices/list')
    },
    onError: (error: Error) => {
      toast.error(`Error updating Purchase Invoice: ${error.message}`)
    }
  })

  // Item search function
  const searchItems = useCallback(async (query: string): Promise<Item[]> => {
    if (!query || query.length < 2) return []
    try {
      const result = await listItems({ search_query: query, limit: 20 })
      return result.items || []
    } catch (error) {
      console.error('Error searching items:', error)
      toast.error(`Error searching items: ${(error as Error).message}`)
      return []
    }
  }, [])

  // Check if the invoice is in a finalized state (paid or cancelled)
  const isInvoiceFinalized =
    purchaseInvoice?.is_cancelled ||
    purchaseInvoice?.status === 'Paid' ||
    purchaseInvoice?.status === 'Posted' ||
    purchaseInvoice?.payment_status === 'Paid'

  // Get status information for display
  const simplifiedStatus = getSimplifiedSalesInvoiceStatus(purchaseInvoice)

  const getDisplayBadgeVariant = (): BadgeProps['variant'] | null => {
    if (!purchaseInvoice) return null
    switch (simplifiedStatus) {
      case 'Paid':
        return 'success'
      case 'Partially Paid':
        return 'warning'
      case 'Unpaid':
        return 'outline'
      case 'Cancelled':
        return 'destructive'
      default:
        return 'secondary'
    }
  }

  const statusBadgeVariant = getDisplayBadgeVariant()

  // Check if invoice can be marked as paid
  const canMarkAsPaid =
    purchaseInvoice && !purchaseInvoice.is_cancelled && simplifiedStatus !== 'Paid'

  // Handler for marking invoice as paid
  const handleMarkAsPaid = () => {
    if (purchaseInvoice) {
      setIsPaymentModalOpen(true)
    }
  }

  // Handler for creating Debit Note
  const handleCreateDebitNote = () => {
    if (!id || !purchaseInvoice) return
    toast.info('Redirecting to create Debit Note...')
    navigate(`/debit-notes/new?fromInvoiceId=${id}`)
  }

  // Handle form submission
  const handleSubmit = (data: PurchaseInvoiceFormValues) => {
    console.log('[page handleSubmit] Called with data:', data);
    console.log('[page handleSubmit] isEditMode:', isEditMode);
    console.log('[page handleSubmit] id:', id);
    
    if (isInvoiceFinalized) {
      toast.error('Cannot update a finalized invoice (Paid or Cancelled)')
      return
    }

    if (isEditMode && id) {
      console.log('[page handleSubmit] Calling updateMutation');
      updateMutation.mutate({ id: Number(id), data: data as unknown as VoucherUpdate })
    } else {
      console.log('[page handleSubmit] Calling createMutation');
      createMutation.mutate(data as unknown as VoucherCreate)
    }
  }

  const isLoading =
    isLoadingVoucherTypes ||
    isLoadingParties ||
    isLoadingSites ||
    isLoadingGodowns ||
    (isEditMode && isLoadingPurchaseInvoice)
  const isSubmitting = createMutation.isPending || updateMutation.isPending

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin h-8 w-8" />
      </div>
    )
  }

  // Render error state
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Error loading data: {(error as Error).message}</AlertDescription>
      </Alert>
    )
  }

  // Check if voucher type exists
  if (!purchaseInvoiceVoucherTypeId) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Purchase Invoice Type (PINV) not configured.</AlertDescription>
      </Alert>
    )
  }

  // Define badge variant type explicitly
  type BadgeVariant = BadgeProps['variant']

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">
            {isEditMode
              ? `Edit Purchase Invoice ${purchaseInvoice?.voucher_number}`
              : 'Create Purchase Invoice'}
          </h1>

          {/* Display invoice status */}
          {statusBadgeVariant && (
            <Badge variant={statusBadgeVariant as BadgeVariant} className="ml-2">
              {simplifiedStatus}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Mark as Paid Button */}
          {canMarkAsPaid && (
            <Button variant="default" size="sm" onClick={handleMarkAsPaid} disabled={isSubmitting}>
              <CreditCard className="h-4 w-4 mr-2" />
              Mark as Paid
            </Button>
          )}

          {/* Create Debit Note Button */}
          {isEditMode && purchaseInvoice && !purchaseInvoice.is_cancelled && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCreateDebitNote}
              disabled={isSubmitting}
            >
              <Undo2 className="h-4 w-4 mr-2" />
              Create Debit Note
            </Button>
          )}
        </div>
      </div>

      {/* Show warning if invoice is finalized */}
      {isInvoiceFinalized && (
        <Alert variant="destructive">
          <AlertDescription>
            This invoice has been {purchaseInvoice?.is_cancelled ? 'cancelled' : 'paid'} and cannot
            be modified.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="pt-6">
          <PurchaseInvoiceForm
            purchaseInvoice={purchaseInvoice || undefined}
            parties={partiesData.items}
            sites={sitesData.items}
            godowns={godownsData.items}
            voucherTypeId={purchaseInvoiceVoucherTypeId}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            searchItems={searchItems}
            isLoadingItems={false}
            isDisabled={isInvoiceFinalized}
          />
        </CardContent>
      </Card>

      {/* Quick Payment Modal */}
      {purchaseInvoice && (
        <QuickPaymentOutModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          invoice={purchaseInvoice}
        />
      )}
    </div>
  )
}

export default PurchaseInvoiceDetailPage
